import React, { useState, useMemo } from "react";
import { motion, useInView } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PageSEO } from "@/components/PageSEO";
import { PageHero } from "@/components/PageHero";
import { useLang } from "@/context/LanguageContext";
import {
  TrendingUp, Calculator, Percent, BarChart3, Building2,
  ChevronRight, ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";

const CALC_HERO = "https://static.prod-images.emergentagent.com/jobs/236e7b77-d75d-43f0-8b66-78eefb5d55fb/images/5bc04a17ce262bb980a4bd640cc508410fd1c54f9eb2cdd446bf57262ce61769.jpeg";

function formatINR(n) {
  if (!n || isNaN(n)) return "\u20B90";
  if (n >= 10000000) return `\u20B9${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `\u20B9${(n / 100000).toFixed(2)} Lakh`;
  return `\u20B9${Math.round(n).toLocaleString("en-IN")}`;
}

/* ── Animated reveal wrapper ── */
function Reveal({ children, className = "", delay = 0 }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── Result Card ── */
function ResultCard({ label, value, accent = false }) {
  return (
    <div className="bg-slate-800/50 border border-white/[0.06] p-3 text-center">
      <div className="text-slate-500 text-xs mb-1">{label}</div>
      <div
        className={`text-lg font-bold ${accent ? "text-orange-500" : "text-white"}`}
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
        data-testid={`result-${label.replace(/\s+/g, "-").toLowerCase()}`}
      >
        {value}
      </div>
    </div>
  );
}

/* ── Input Field ── */
function Field({ label, value, onChange, min, max, step, suffix }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-400 mb-1.5">{label}</label>
      <div className="relative">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={min}
          max={max}
          step={step || 1}
          className="w-full border border-slate-600 px-3 py-2.5 text-sm bg-slate-800 text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">{suffix}</span>}
      </div>
    </div>
  );
}

/* ── Slider ── */
function SliderField({ label, value, onChange, min, max, step, suffix, displayVal }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-1.5">
        <label className="text-xs font-medium text-slate-400">{label}</label>
        <span className="text-sm font-bold text-orange-400" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          {displayVal || value}{suffix}
        </span>
      </div>
      <input
        type="range"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min}
        max={max}
        step={step || 1}
        className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
      />
      <div className="flex justify-between text-xs text-slate-600 mt-0.5">
        <span>{min}{suffix}</span>
        <span>{max}{suffix}</span>
      </div>
    </div>
  );
}

/* ──────────────── ROI Calculator ──────────────── */
function ROICalculator({ t }) {
  const [price, setPrice] = useState(5000000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  const result = useMemo(() => {
    const fv = price * Math.pow(1 + rate / 100, years);
    const totalReturn = fv - price;
    const roi = ((fv / price - 1) * 100).toFixed(1);
    return { futureValue: fv, totalReturn, annualROI: roi };
  }, [price, rate, years]);

  return (
    <div className="bg-[#1E293B] border border-white/10 overflow-hidden" data-testid="roi-calculator">
      <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
        <div className="w-10 h-10 bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-orange-500" />
        </div>
        <div>
          <h3 className="text-white font-bold text-base" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: "uppercase" }}>
            {t("calculator.roiTitle")}
          </h3>
          <p className="text-slate-500 text-xs">{t("calculator.roiDesc")}</p>
        </div>
      </div>
      <div className="p-6 space-y-5">
        <SliderField label={t("calculator.purchasePrice")} value={price} onChange={setPrice}
          min={500000} max={50000000} step={500000} displayVal={formatINR(price)} suffix="" />
        <SliderField label={t("calculator.appreciationRate")} value={rate} onChange={setRate}
          min={5} max={30} step={0.5} suffix="%" />
        <SliderField label={t("calculator.holdingPeriod")} value={years} onChange={setYears}
          min={1} max={30} step={1} suffix={` ${t("calculator.years")}`} />
        <div className="grid grid-cols-3 gap-3 pt-2">
          <ResultCard label={t("calculator.futureValue")} value={formatINR(result.futureValue)} accent />
          <ResultCard label={t("calculator.totalReturn")} value={formatINR(result.totalReturn)} />
          <ResultCard label={t("calculator.annualROI")} value={`${result.annualROI}%`} />
        </div>
      </div>
    </div>
  );
}

/* ──────────────── EMI Calculator ──────────────── */
function EMICalculator({ t }) {
  const [loan, setLoan] = useState(3000000);
  const [interest, setInterest] = useState(9.5);
  const [tenure, setTenure] = useState(15);

  const result = useMemo(() => {
    const r = interest / 12 / 100;
    const n = tenure * 12;
    if (r === 0) return { emi: loan / n, totalInterest: 0, totalPayment: loan };
    const emi = loan * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    const totalPayment = emi * n;
    const totalInterest = totalPayment - loan;
    return { emi, totalInterest, totalPayment };
  }, [loan, interest, tenure]);

  // EMI breakdown bar
  const principalPct = loan / result.totalPayment * 100;

  return (
    <div className="bg-[#1E293B] border border-white/10 overflow-hidden" data-testid="emi-calculator">
      <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
        <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
          <Calculator className="w-5 h-5 text-emerald-500" />
        </div>
        <div>
          <h3 className="text-white font-bold text-base" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: "uppercase" }}>
            {t("calculator.emiTitle")}
          </h3>
          <p className="text-slate-500 text-xs">{t("calculator.emiDesc")}</p>
        </div>
      </div>
      <div className="p-6 space-y-5">
        <SliderField label={t("calculator.loanAmount")} value={loan} onChange={setLoan}
          min={100000} max={50000000} step={100000} displayVal={formatINR(loan)} suffix="" />
        <SliderField label={t("calculator.interestRate")} value={interest} onChange={setInterest}
          min={6} max={18} step={0.25} suffix="%" />
        <SliderField label={t("calculator.loanTenure")} value={tenure} onChange={setTenure}
          min={1} max={30} step={1} suffix={` ${t("calculator.years")}`} />
        {/* Breakdown bar */}
        <div>
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>Principal</span>
            <span>Interest</span>
          </div>
          <div className="h-3 bg-slate-700 flex overflow-hidden">
            <div className="bg-emerald-500 transition-all duration-500" style={{ width: `${principalPct}%` }} />
            <div className="bg-orange-500 transition-all duration-500" style={{ width: `${100 - principalPct}%` }} />
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>{principalPct.toFixed(0)}%</span>
            <span>{(100 - principalPct).toFixed(0)}%</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 pt-2">
          <ResultCard label={t("calculator.monthlyEMI")} value={formatINR(result.emi)} accent />
          <ResultCard label={t("calculator.totalInterest")} value={formatINR(result.totalInterest)} />
          <ResultCard label={t("calculator.totalPayment")} value={formatINR(result.totalPayment)} />
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Price Appreciation Forecast ──────────────── */
function AppreciationCalculator({ t }) {
  const [size, setSize] = useState(1000);
  const [rate, setRate] = useState(6000);
  const [growth, setGrowth] = useState(10);

  const data = useMemo(() => {
    const currentVal = size * rate;
    return [5, 10, 15, 20].map(yr => {
      const fv = currentVal * Math.pow(1 + growth / 100, yr);
      return { year: yr, value: fv, gain: fv - currentVal, pct: ((fv / currentVal - 1) * 100).toFixed(0) };
    });
  }, [size, rate, growth]);

  const maxVal = Math.max(...data.map(d => d.value));

  return (
    <div className="bg-[#1E293B] border border-white/10 overflow-hidden" data-testid="appreciation-calculator">
      <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-blue-500" />
        </div>
        <div>
          <h3 className="text-white font-bold text-base" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: "uppercase" }}>
            {t("calculator.appreciationTitle")}
          </h3>
          <p className="text-slate-500 text-xs">{t("calculator.appreciationDesc")}</p>
        </div>
      </div>
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-3 gap-3">
          <Field label={t("calculator.plotSize")} value={size} onChange={setSize} min={100} max={10000} />
          <Field label={t("calculator.currentRate")} value={rate} onChange={setRate} min={3000} max={15000} />
          <SliderField label={t("calculator.appreciationRate")} value={growth} onChange={setGrowth} min={5} max={25} step={0.5} suffix="%" />
        </div>
        <div className="bg-slate-800/50 border border-white/[0.06] p-4">
          <div className="text-xs text-slate-500 mb-1">{t("calculator.projectedValue")} (Current)</div>
          <div className="text-xl font-bold text-white mb-4" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {formatINR(size * rate)}
          </div>
          {/* Bar chart */}
          <div className="space-y-3">
            {data.map(d => (
              <div key={d.year} className="flex items-center gap-3">
                <span className="w-14 text-right text-xs text-slate-500 flex-shrink-0" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {d.year} {t("calculator.years")}
                </span>
                <div className="flex-1 h-7 bg-slate-700/50 relative overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-blue-600 to-orange-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${(d.value / maxVal) * 100}%` }}
                    transition={{ duration: 0.8, delay: d.year * 0.05 }}
                  />
                  <div className="absolute inset-0 flex items-center px-2">
                    <span className="text-white text-xs font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {formatINR(d.value)}
                    </span>
                  </div>
                </div>
                <span className="text-emerald-400 text-xs font-bold w-12 text-right" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  +{d.pct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Rental Yield Calculator ──────────────── */
function RentalCalculator({ t }) {
  const [propValue, setPropValue] = useState(5000000);
  const [rent, setRent] = useState(25000);

  const result = useMemo(() => {
    const annualRent = rent * 12;
    const yieldPct = propValue > 0 ? (annualRent / propValue * 100).toFixed(2) : 0;
    const payback = rent > 0 ? (propValue / annualRent).toFixed(1) : 0;
    return { annualRent, yieldPct, payback };
  }, [propValue, rent]);

  return (
    <div className="bg-[#1E293B] border border-white/10 overflow-hidden" data-testid="rental-calculator">
      <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
        <div className="w-10 h-10 bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-purple-500" />
        </div>
        <div>
          <h3 className="text-white font-bold text-base" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: "uppercase" }}>
            {t("calculator.rentalTitle")}
          </h3>
          <p className="text-slate-500 text-xs">{t("calculator.rentalDesc")}</p>
        </div>
      </div>
      <div className="p-6 space-y-5">
        <SliderField label={t("calculator.propertyValue")} value={propValue} onChange={setPropValue}
          min={500000} max={50000000} step={500000} displayVal={formatINR(propValue)} suffix="" />
        <SliderField label={t("calculator.monthlyRent")} value={rent} onChange={setRent}
          min={5000} max={500000} step={5000} displayVal={formatINR(rent)} suffix={t("calculator.perMonth")} />
        <div className="grid grid-cols-3 gap-3 pt-2">
          <ResultCard label={t("calculator.annualYield")} value={`${result.yieldPct}%`} accent />
          <ResultCard label={t("calculator.paybackPeriod")} value={`${result.payback} ${t("calculator.years")}`} />
          <ResultCard label="Annual Rent" value={formatINR(result.annualRent)} />
        </div>
        {/* Yield indicator */}
        <div className={`text-center py-2 text-sm font-bold ${
          result.yieldPct >= 6 ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" :
          result.yieldPct >= 4 ? "bg-orange-500/10 text-orange-400 border border-orange-500/30" :
          "bg-red-500/10 text-red-400 border border-red-500/30"
        }`}>
          {result.yieldPct >= 6 ? "Excellent Yield" : result.yieldPct >= 4 ? "Good Yield" : "Below Average Yield"}
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Main Page ──────────────── */
export default function CalculatorPage() {
  const { t } = useLang();

  return (
    <div className="bg-[#0F172A]">
      <PageSEO page="calculator" />
      <Navbar />

      {/* Hero */}
      <PageHero
        image={CALC_HERO}
        tag="Investment Tools"
        title={t("calculator.title")}
        subtitle={t("calculator.subtitle")}
        breadcrumb={{ home: "Home", current: t("calculator.breadcrumb") }}
      />

      {/* Calculators Grid */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Reveal>
              <ROICalculator t={t} />
            </Reveal>
            <Reveal delay={0.1}>
              <EMICalculator t={t} />
            </Reveal>
            <Reveal delay={0.15}>
              <AppreciationCalculator t={t} />
            </Reveal>
            <Reveal delay={0.2}>
              <RentalCalculator t={t} />
            </Reveal>
          </div>
        </div>
      </div>

      {/* CTA */}
      <Reveal>
        <div className="bg-[#1E293B] border-y border-white/10 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2
              className="text-white mb-3"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: "clamp(1.5rem, 3vw, 2.5rem)", textTransform: "uppercase" }}
            >
              Ready to Invest in Industrial Land?
            </h2>
            <p className="text-slate-400 mb-6 max-w-xl mx-auto">
              Contact our team for personalized investment advice and plot availability.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/plots"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2 transition-all hover:shadow-[0_0_20px_rgba(249,115,22,0.3)]"
                data-testid="calc-view-plots-btn">
                View Available Plots <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/booking"
                className="border border-white/20 text-white hover:border-orange-500 px-8 py-3 font-bold uppercase tracking-wider text-sm transition-all"
                data-testid="calc-book-btn">
                Book a Plot
              </Link>
              <a href="tel:+919258959058"
                className="border border-white/20 text-white hover:border-orange-500 px-8 py-3 font-bold uppercase tracking-wider text-sm transition-all">
                Call +91-9258959058
              </a>
            </div>
          </div>
        </div>
      </Reveal>

      <Footer />
    </div>
  );
}
